import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Button,
  EmptyState,
  CircularLoader,
  Banner,
  LockIcon,
  RefreshIcon,
  useDocumentTitle,
} from '@forge/common';
import { useExpiringKeys } from '@/hooks/usePartnerKeys';
import { usePartners } from '@/hooks/usePartners';
import { useApiMutation } from '@/hooks/useApiMutation';
import { partnerKeysApi } from '@/api/partnerKeys';
import type { SweepExpiringKeysResult } from '@/api/partnerKeys';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';

const KeyringPage = () => {
  useDocumentTitle('Keyring — Partner Exchange — OneRing Portal');
  const navigate = useNavigate();
  const [days] = useState(30);

  const { partners } = usePartners();
  const { keys, isLoading, error, isUsingMockData, reload } = useExpiringKeys(days);

  const partnerNameById = useMemo(
    () => new Map(partners.map((p) => [p.id, p.displayName])),
    [partners],
  );

  const { mutate: runSweep, isSubmitting } = useApiMutation<number, SweepExpiringKeysResult>({
    mutationFn: (d) => partnerKeysApi.sweepExpiring(d),
    successMessage: 'Expiry sweep completed',
    errorMessage: 'Expiry sweep failed',
    onSuccess: () => reload(),
  });

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Keyring</h2>
      <p className="text-sm text-color-tertiary mb-4">
        Keys expiring within the next {days} days, across every partner. The same check runs
        automatically each morning via onering-crons' <code>pgp-key-expiry.yml</code> — this is
        the same view plus a manual trigger for ad-hoc checks.
      </p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="Keyring" />
        </div>
      )}

      {error ? (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load expiring keys"
          description={error}
          canClose={false}
        />
      ) : (
        <Card>
          <CardHeader
            title="Expiring Keys"
            subtitle={`${keys.length} key${keys.length !== 1 ? 's' : ''} expiring soon`}
            slotRight={
              <Button
                variant="secondary"
                icon={<RefreshIcon />}
                onClick={() => runSweep(days)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Running...' : 'Run Sweep Now'}
              </Button>
            }
          />
          <CardContent>
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center">
                <CircularLoader size="large" />
              </div>
            ) : keys.length === 0 ? (
              <EmptyState icon={<LockIcon />} description="No keys expiring soon" />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Fingerprint</TableCell>
                    <TableCell>Expires</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((key) => (
                    <TableRow
                      key={key.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => navigate(`/partners/${key.partnerId}`)}
                    >
                      <TableCell>{partnerNameById.get(key.partnerId) ?? key.partnerId}</TableCell>
                      <TableCell>
                        {key.role === 'partner_public' ? 'Partner public' : 'Honeywell keypair'}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono break-all">{key.fingerprint}</span>
                      </TableCell>
                      <TableCell>
                        <Badge color="orange">
                          {key.expiresAt ? new Date(key.expiresAt).toLocaleDateString() : '—'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KeyringPage;
